/**
 * Hook quản lý danh sách BĐS trong khu admin.
 *
 * Đồng bộ filter với URL, debounce ô tìm kiếm, phân trang, xóa và duyệt tin.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { propertyApi } from '../../../properties/api/propertyApi';
import { TRANSACTION_TYPES } from '../../../../shared/constants/property';
import { buildSearchParamsFromAdminFilters } from '../../../../shared/utils/adminFilterSearchParams';

const defaultFilters = {
  search: '',
  status: '',
  publication_status: '',
  page: '1',
};

const buildFiltersFromSearchParams = (searchParams) => ({
  ...defaultFilters,
  search: searchParams.get('search') || '',
  status: searchParams.get('status') || '',
  publication_status: searchParams.get('publication_status') || '',
  page: searchParams.get('page') || '1',
});

const removeEmptyFilters = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => {
      if (!value) {
        return false;
      }

      return key !== 'page' || value !== '1';
    })
  );

/**
 * Hook bảng quản lý tin: lọc, tìm kiếm, phân trang, xóa/duyệt.
 *
 * @returns {Object} propertyList, pagination, filters, handlers CRUD workflow
 */
export const useAdminProperties = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const [propertyList, setPropertyList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [filters, setFilters] = useState(() => buildFiltersFromSearchParams(searchParams));
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [deletingPropertyId, setDeletingPropertyId] = useState(null);
  const [approvingPropertyId, setApprovingPropertyId] = useState(null);

  // URL thay đổi (back/forward): đồng bộ state filter và ô search
  useEffect(() => {
    const urlFilters = buildFiltersFromSearchParams(searchParams);
    setFilters(urlFilters);
    setSearchInput(urlFilters.search);
  }, [searchParamsKey, searchParams]);

  // State filter thay đổi: phản chiếu lên URL (replace, tránh stack history dài)
  useEffect(() => {
    const nextParams = buildSearchParamsFromAdminFilters(filters);
    const nextParamsKey = nextParams.toString();

    if (nextParamsKey !== searchParamsKey) {
      setSearchParams(nextParams, { replace: true });
    }
  }, [filters, searchParamsKey, setSearchParams]);

  const activeFilters = useMemo(
    () => ({
      ...removeEmptyFilters(filters),
      transaction_type: TRANSACTION_TYPES.sale,
    }),
    [filters]
  );

  const loadPropertyList = useCallback(() => {
    setIsLoadingProperties(true);

    return propertyApi
      .getAdminProperties(activeFilters)
      .then((response) => {
        setPropertyList(response.data.data || []);
        setPagination(response.data.pagination || null);
      })
      .finally(() => setIsLoadingProperties(false));
  }, [activeFilters]);

  // Debounce 300ms: gõ search không gọi API từng ký tự
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setFilters((current) => {
        if (current.search === searchInput.trim()) {
          return current;
        }

        return {
          ...current,
          search: searchInput.trim(),
          page: '1',
        };
      });
    }, 300);

    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadPropertyList();
  }, [loadPropertyList]);

  const updateFilter = (field, value) => {
    setFilters((current) => ({
      ...current,
      [field]: value,
      page: '1',
    }));
  };

  const resetFilters = () => {
    setSearchInput('');
    setFilters(defaultFilters);
  };

  const changePage = (page) => {
    setFilters((current) => ({
      ...current,
      page: String(page),
    }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const deleteProperty = async (propertyId) => {
    setDeletingPropertyId(propertyId);

    try {
      await propertyApi.deleteProperty(propertyId);
      await loadPropertyList();
    } finally {
      setDeletingPropertyId(null);
    }
  };

  const approveProperty = async (propertyId) => {
    setApprovingPropertyId(propertyId);

    try {
      await propertyApi.approveProperty(propertyId);
      await loadPropertyList();
    } finally {
      setApprovingPropertyId(null);
    }
  };

  const hasActiveFilters = Boolean(
    searchInput.trim() || filters.status || filters.publication_status
  );

  return {
    propertyList,
    pagination,
    filters,
    searchInput,
    setSearchInput,
    isLoadingProperties,
    deletingPropertyId,
    approvingPropertyId,
    hasActiveFilters,
    updateFilter,
    resetFilters,
    changePage,
    deleteProperty,
    approveProperty,
    reloadPropertyList: loadPropertyList,
  };
};
